import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getApiBaseUrl } from '../../utils/apiConfig';

// Types
interface Transaction {
  id: number;
  type: string;
  amount: number;
  transactionDate: string;
  description: string;
  category: string;
  status: string;
}

interface TransactionState {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
}

// Initial state
const initialState: TransactionState = {
  transactions: [],
  isLoading: false,
  error: null,
  currentPage: 0,
  totalPages: 0,
  hasMore: true,
};

// Async thunk
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchData',
  async ({ page = 0, size = 10 }: { page?: number; size?: number }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; type: 'JWT' | 'SESSION' | null } };
      const token = state.auth.token;
      const authType = state.auth.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${getApiBaseUrl()}/transactions?page=${page}&size=${size}`, {
        method: 'GET',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
      });

      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to fetch transactions');
      }
      return json.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

// Slice
const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    clearTransactionError: (state) => {
      state.error = null;
    },
    addTransaction: (state, action: PayloadAction<Transaction>) => {
      state.transactions.unshift(action.payload);
    },
    updateTransaction: (state, action: PayloadAction<Transaction>) => {
      const index = state.transactions.findIndex(t => t.id === action.payload.id);
      if (index !== -1) {
        state.transactions[index] = action.payload;
      }
    },
    clearTransactions: (state) => {
      state.transactions = [];
      state.currentPage = 0;
      state.totalPages = 0;
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        
        // If it's the first page, replace transactions
        if (action.meta.arg.page === 0) {
          state.transactions = action.payload.content || action.payload;
        } else {
          // Otherwise, append to existing transactions
          state.transactions = [...state.transactions, ...(action.payload.content || action.payload)];
        }
        
        state.currentPage = action.meta.arg.page || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.hasMore = action.payload.content ? action.payload.content.length === action.meta.arg.size : true;
        state.error = null;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearTransactionError, 
  addTransaction, 
  updateTransaction, 
  clearTransactions 
} = transactionSlice.actions;
export default transactionSlice.reducer; 